// src/lib/axiosConfig.js - SIMPLIFIED VERSION

import axios from "axios";

let interceptorId = null;

export const setupAxiosInterceptors = (auth) => {
  // Remove existing interceptor if it exists
  if (interceptorId !== null) {
    axios.interceptors.request.eject(interceptorId);
  }

  // Request interceptor
  interceptorId = axios.interceptors.request.use(
    (config) => {
      // Skip ALL checks if logging in or logging out
      if (sessionStorage.getItem("logging_in") === "true" || sessionStorage.getItem("logging_out") === "true") {
        return config;
      }

      // Skip for public paths
      const currentPath = window.location.pathname;
      if (
        currentPath === "/" ||
        currentPath.includes("/callback") ||
        currentPath.includes("/uploadFile") ||
        currentPath.includes("/tabsFileShare")
      ) {
        return config;
      }

      // Update activity and check auth
      if (auth && auth.isAuthenticated && auth.isAuthenticated()) {
        // Background requests (e.g. timer-driven token refresh) must not reset the idle clock.
        if (auth.updateActivity && !config.background) {
          auth.updateActivity();
        }
        return config;
      }

      // If not authenticated and not on a public page, block request. This is
      // the client already knowing its token is past expires_at - unlike the
      // response interceptor below, there's no error.response here for it to
      // react to, so this is the only place that can trigger the redirect
      // for this case; without it, the request is silently blocked and the
      // user is left on the page with no path back to login.
      if (auth && !auth.isAuthenticated()) {
        console.warn("Session expired - blocking request");
        auth.logout("unauthorized", { fullLogout: false });
        return Promise.reject(new Error("Session expired"));
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Skip if logging in/out
      if (sessionStorage.getItem("logging_in") === "true" || sessionStorage.getItem("logging_out") === "true") {
        return Promise.reject(error);
      }

      // The server returns 401 when a session was explicitly revoked (logout/
      // idle-timeout on another tab), but a naturally-expired clientToken -
      // the common case - fails jwt.verify's exp check and comes back as 403
      // "#1/#3 Invalid Token" / "Invalid Token from App Auth 1". Both mean
      // the same thing to the user: re-authenticate. Without also catching
      // the 403 case here, an expired token just silently fails every
      // request instead of ever prompting a fresh login.
      const isRevoked = error.response && error.response.status === 401;
      const isExpiredOrInvalidToken =
        error.response &&
        error.response.status === 403 &&
        error.response.data &&
        typeof error.response.data.message === "string" &&
        error.response.data.message.includes("Invalid Token");

      if ((isRevoked || isExpiredOrInvalidToken) && auth) {
        // A stale/deleted directory session (server returns code STALE_SESSION):
        // the AAD token itself is invalid, so an app-only logout would loop.
        // Force a full Microsoft logout with the remediation message.
        if (error.response.data && error.response.data.code === "STALE_SESSION") {
          auth.handleStaleSession(error.response.data.message);
        } else {
          auth.logout("unauthorized", { fullLogout: false });
        }
      }
      return Promise.reject(error);
    },
  );
};
